import type { Order } from "../../services/ordersApi";

// Order responses use enum names (JsonStringEnumConverter), while the
// amendment quantity logic uses the numeric values declared in ordersApi.
const pricingModes: Record<string, number> = {
  PerTray: 1,
  PerSeedlingOrPiece: 2,
};
const units: Record<string, number> = {
  Unspecified: 0,
  Kilograms: 1, Kilogram: 1,
  Grams: 2, Gram: 2,
  Liters: 3, Liter: 3,
  Milliliters: 4, Milliliter: 4,
  Pieces: 5, Piece: 5,
  Boxes: 6, Box: 6,
  Bags: 7, Bag: 7,
  Packets: 8, Packet: 8,
  Bottles: 9, Bottle: 9,
  Cans: 10, Can: 10,
  Trays: 11, Tray: 11,
  SeedCount: 12,
};
const fulfillmentSources: Record<string, number> = {
  Unknown: 0,
  FromStock: 1,
  PartialStock: 2,
  NeedsNewSowing: 3,
  CustomerSeeds: 4,
  FromExpo: 5,
};

const enumNumber = (value: unknown, names: Record<string, number>): number => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return 0;
  return names[value] ?? (Number.isFinite(Number(value)) ? Number(value) : 0);
};

export const normalizeOrderItemEnums = (order: Order): Order => ({
  ...order,
  orderItems: order.orderItems.map((item) => ({
    ...item,
    pricingMode: enumNumber(item.pricingMode, pricingModes),
    unit: enumNumber(item.unit, units),
    fulfillmentSource: enumNumber(item.fulfillmentSource, fulfillmentSources),
  })),
});

export const isTrayBackedWalkInPieceLine = (
  order: Order,
  item: Order["orderItems"][number],
) => order.isWalkIn === true
  && item.pricingMode === 2
  && item.unit === 11
  && item.trayCapacity != null
  && item.sellablePerTray != null;

export const getPiecesPerStoredTray = (order: Order, item: Order["orderItems"][number]): number => {
  if (!isTrayBackedWalkInPieceLine(order, item)) return 1;

  const physicalCapacity = item.trayCapacity!;
  const sellableCapacity = item.sellablePerTray!;
  const storedQuantity = Number(item.quantity ?? 0);
  const unitPrice = Number(item.unitPrice ?? 0);
  const storedSubtotal = Number(item.subTotal ?? 0);
  const sellableSubtotal = Math.round(storedQuantity * sellableCapacity) * unitPrice;
  const physicalSubtotal = Math.round(storedQuantity * physicalCapacity) * unitPrice;

  // Match the immutable line subtotal, like the server's historical walk-in logic.
  return Math.abs(storedSubtotal - physicalSubtotal) + 0.000001
    < Math.abs(storedSubtotal - sellableSubtotal)
    ? physicalCapacity
    : sellableCapacity;
};

export const toCommercialQuantity = (
  order: Order,
  item: Order["orderItems"][number],
  storedQuantity: number,
): number => isTrayBackedWalkInPieceLine(order, item)
  ? Math.round(storedQuantity * getPiecesPerStoredTray(order, item))
  : storedQuantity;

export const toStoredQuantity = (
  order: Order,
  item: Order["orderItems"][number],
  commercialQuantity: number,
): number => isTrayBackedWalkInPieceLine(order, item)
  ? Math.round((commercialQuantity / getPiecesPerStoredTray(order, item)) * 1_000_000) / 1_000_000
  : commercialQuantity;
